import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from "react";

import type { DocumentsTableItemId } from "./DocumentsTable.dnd";

type SelectionDirection = "down" | "up";

const DOCUMENTS_TABLE_INTERACTIVE_TARGET_SELECTOR =
  'a[href], button, input, select, summary, textarea, [role="button"], [role="checkbox"], [role="link"], [role="menuitem"], [role="option"], [role="radio"], [role="switch"], [role="tab"], [role="textbox"]';
const DOCUMENTS_TABLE_KEYBOARD_CONTAINER_SELECTOR =
  '[role="menu"], [role="dialog"], [role="combobox"], [role="listbox"], [role="columnheader"]';

const isMacPlatform = () =>
  typeof navigator !== "undefined" &&
  navigator.platform.toLowerCase().includes("mac");

export const isDocumentsTableSelectionShortcutEvent = (
  event: Pick<MouseEvent | KeyboardEvent, "ctrlKey" | "metaKey">
) => (isMacPlatform() ? event.metaKey : event.ctrlKey || event.metaKey);

export const isDocumentsTableInteractiveEventTarget = (
  target: EventTarget | null
) =>
  target instanceof Element &&
  ((target instanceof HTMLElement && target.isContentEditable) ||
    Boolean(target.closest(DOCUMENTS_TABLE_INTERACTIVE_TARGET_SELECTOR)));

export const shouldIgnoreDocumentsTableKeyboardEvent = (
  event: KeyboardEvent
) => {
  const target = event.target;

  if (!(target instanceof Element)) {
    return false;
  }

  return (
    isDocumentsTableInteractiveEventTarget(target) ||
    Boolean(target.closest(DOCUMENTS_TABLE_KEYBOARD_CONTAINER_SELECTOR))
  );
};

const getRangeItemIds = (
  visibleItemIds: DocumentsTableItemId[],
  anchorItemId: DocumentsTableItemId,
  targetItemId: DocumentsTableItemId
) => {
  const anchorIndex = visibleItemIds.indexOf(anchorItemId);
  const targetIndex = visibleItemIds.indexOf(targetItemId);

  if (anchorIndex === -1 || targetIndex === -1) {
    return [targetItemId];
  }

  return visibleItemIds.slice(
    Math.min(anchorIndex, targetIndex),
    Math.max(anchorIndex, targetIndex) + 1
  );
};

const moveFocus = (
  visibleItemIds: DocumentsTableItemId[],
  focusedItemId: DocumentsTableItemId | null,
  direction: SelectionDirection
) => {
  if (visibleItemIds.length === 0) {
    return null;
  }

  const currentIndex = focusedItemId
    ? visibleItemIds.indexOf(focusedItemId)
    : -1;

  if (currentIndex === -1) {
    return visibleItemIds[0];
  }

  return visibleItemIds[
    direction === "down"
      ? Math.min(currentIndex + 1, visibleItemIds.length - 1)
      : Math.max(currentIndex - 1, 0)
  ];
};

export const useDocumentsTableSelection = ({
  disabled,
  visibleItemIds,
}: {
  disabled: boolean;
  visibleItemIds: DocumentsTableItemId[];
}) => {
  const visibleItemIdSet = useMemo(
    () => new Set(visibleItemIds),
    [visibleItemIds]
  );
  const [selectedItemIds, setSelectedItemIds] = useState<
    DocumentsTableItemId[]
  >([]);
  const [anchorItemId, setAnchorItemId] = useState<DocumentsTableItemId | null>(
    null
  );
  const [focusedItemId, setFocusedItemId] =
    useState<DocumentsTableItemId | null>(null);

  useEffect(() => {
    setSelectedItemIds((previousItemIds) => {
      const nextItemIds = previousItemIds.filter((itemId) =>
        visibleItemIdSet.has(itemId)
      );

      return nextItemIds.length === previousItemIds.length
        ? previousItemIds
        : nextItemIds;
    });
    setAnchorItemId((itemId) =>
      itemId && visibleItemIdSet.has(itemId) ? itemId : null
    );
    setFocusedItemId((itemId) =>
      itemId && visibleItemIdSet.has(itemId) ? itemId : null
    );
  }, [visibleItemIdSet]);

  const clearTemporaryMultiSelection = useCallback(() => {
    setSelectedItemIds((previousItemIds) =>
      previousItemIds.length > 1 ? [] : previousItemIds
    );
    setAnchorItemId(null);
  }, []);

  useEffect(() => {
    const handleSelectionKeyUp = (event: globalThis.KeyboardEvent) => {
      if (
        ["Control", "Meta", "Shift"].includes(event.key) &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey
      ) {
        clearTemporaryMultiSelection();
      }
    };

    window.addEventListener("keyup", handleSelectionKeyUp);
    window.addEventListener("blur", clearTemporaryMultiSelection);

    return () => {
      window.removeEventListener("keyup", handleSelectionKeyUp);
      window.removeEventListener("blur", clearTemporaryMultiSelection);
    };
  }, [clearTemporaryMultiSelection]);

  const clearSelection = useCallback(() => {
    setSelectedItemIds([]);
    setAnchorItemId(null);
  }, []);

  const replaceSelection = useCallback((itemId: DocumentsTableItemId) => {
    setSelectedItemIds([itemId]);
    setAnchorItemId(itemId);
    setFocusedItemId(itemId);
  }, []);

  const toggleSelection = useCallback((itemId: DocumentsTableItemId) => {
    setSelectedItemIds((previousItemIds) =>
      previousItemIds.includes(itemId)
        ? previousItemIds.filter((selectedItemId) => selectedItemId !== itemId)
        : [...previousItemIds, itemId]
    );
    setAnchorItemId(itemId);
    setFocusedItemId(itemId);
  }, []);

  const selectRange = useCallback(
    (itemId: DocumentsTableItemId, additive: boolean) => {
      const nextAnchorItemId = anchorItemId ?? focusedItemId ?? itemId;
      const rangeItemIds = getRangeItemIds(
        visibleItemIds,
        nextAnchorItemId,
        itemId
      );

      setSelectedItemIds((previousItemIds) =>
        additive
          ? [...new Set([...previousItemIds, ...rangeItemIds])]
          : rangeItemIds
      );
      setAnchorItemId(nextAnchorItemId);
      setFocusedItemId(itemId);
    },
    [anchorItemId, focusedItemId, visibleItemIds]
  );

  const handleRowClickSelection = useCallback(
    (itemId: DocumentsTableItemId, event: MouseEvent) => {
      if (disabled) {
        return;
      }

      const isShortcut = isDocumentsTableSelectionShortcutEvent(event);

      if (event.shiftKey) {
        selectRange(itemId, isShortcut);
        return;
      }

      if (isShortcut) {
        toggleSelection(itemId);
        return;
      }

      replaceSelection(itemId);
    },
    [disabled, replaceSelection, selectRange, toggleSelection]
  );

  const selectAllVisible = useCallback(() => {
    setSelectedItemIds(visibleItemIds);
    setAnchorItemId(visibleItemIds[0] ?? null);
    setFocusedItemId(visibleItemIds[0] ?? null);
  }, [visibleItemIds]);

  const moveFocusedRow = useCallback(
    (direction: SelectionDirection) => {
      setFocusedItemId(moveFocus(visibleItemIds, focusedItemId, direction));
    },
    [focusedItemId, visibleItemIds]
  );

  const extendSelectionByKeyboard = useCallback(
    ({
      additive,
      direction,
    }: {
      additive: boolean;
      direction: SelectionDirection;
    }) => {
      const nextFocusedItemId = moveFocus(
        visibleItemIds,
        focusedItemId,
        direction
      );

      if (nextFocusedItemId) {
        selectRange(nextFocusedItemId, additive);
      }
    },
    [focusedItemId, selectRange, visibleItemIds]
  );

  const selectedItemIdSet = useMemo(
    () => new Set(selectedItemIds),
    [selectedItemIds]
  );

  return {
    clearSelection,
    extendSelectionByKeyboard,
    focusedItemId,
    handleRowClickSelection,
    moveFocusedRow,
    replaceSelection,
    selectAllVisible,
    selectedItemIds,
    selectedItemIdSet,
    setFocusedItemId,
    toggleSelection,
  };
};
